import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, interval } from 'rxjs';
import {
    ClientAlertTypeModel,
    UserAlertTypeModel,
    AlertModel,
    AlertTypeModel,
} from 'app/modules/settings/interfaces/notification';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/alert').replace('{version}', this.version);

    private unreadNotificationCount = new BehaviorSubject(0);

    private notificationBank: AlertModel[];
    private notificationAlert: AlertModel;

    constructor(private http: HttpClient) {
        setInterval(() => {
            this.updateNotificationBank();
        }, 60000);
    }

    public getUnreadNotificationCount(): Observable<number> {
        return this.unreadNotificationCount.asObservable();
    }

    private updateUnreadCount() {
        this.unreadNotificationCount.next(
            this.notificationBank.filter((x) => !x.isRead && !x.isDeleted).length
        );
    }

    public getNotifications(): Observable<any[]> {
        return new Observable((subscriber) => {
            this.http.get<any[]>(`${this._url}`).subscribe(
                (data) => {
                    this.notificationBank = data;
                    this.updateUnreadCount();
                    subscriber.next(data);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    public getNotification(alertKey: number): Observable<AlertModel> {
        return new Observable((subscriber) => {
            this.http.get<AlertModel>(`${this._url}/alertByAlertKey/${alertKey}`).subscribe(
                (data) => {
                    this.notificationAlert = data;
                    subscriber.next(data);
                },
                (response) => {
                    this.notificationAlert = null;
                    subscriber.error(response);
                }
            );
        });
    }

    public getNotificationAlert(): AlertModel {
        return this.notificationAlert;
    }

    public clearNotificationAlert(): void {
        this.notificationAlert = null;
    }

    public updateAlert(alert: AlertModel): Observable<any> {
        let formattedModel = {
            alertTypeKey: alert.alertTypeKey,
            alertEventKey: alert.alertEventKey,
            isDeleted: alert.isDeleted,
            IsRead: alert.isRead,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(`${this._url}/alert`, formattedModel).subscribe(
                () => {
                    this.updateNotificationBank();
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    public updateClientAlertType(alert: ClientAlertTypeModel): Observable<any> {
        let formattedModel = {
            alertTypeKey: alert.alertTypeKey,
            isInactive: alert.isInactive,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(`${this._url}/types/client`, formattedModel).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    public updateClientAlertTypeLBAControl(alert: ClientAlertTypeModel): Observable<any> {
        let formattedModel = {
            alertTypeKey: alert.alertTypeKey,
            isActiveSuperUserControl: alert.isActiveSuperUserControl,
            isInactive: alert.isInactive,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(`${this._url}/types/clientlbacontrol`, formattedModel).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    public upsertUserAlertType(alert: UserAlertTypeModel): Observable<any> {
        let formattedModel = {
            alertName: alert.alertName,
            alertTypeKey: alert.alertTypeKey,
            isInactive: alert.isInactive,
            useEmail: alert.useEmail,
            useText: alert.useText,
        };

        return new Observable((subscriber) => {
            this.http.post<any>(`${this._url}/types/user`, formattedModel).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    public deleteAlert(alertKey: number): Observable<any> {
        const params = new HttpParams().set('alertKey', alertKey.toString());
        return this.http.delete<any>(`${this._url}/alert`, { params: params });
    }

    public deleteUserAlertType(alertTypeKey: number): Observable<any> {
        const params = new HttpParams().set('alertTypeKey', alertTypeKey.toString());
        return this.http.delete<any>(`${this._url}/types/user`, { params: params });
    }

    public updateNotificationBank() {
        this.getNotifications().subscribe(
            (data) => {
                this.notificationBank = data;
                this.updateUnreadCount();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    public getAlertType(alertTypeKey: number): Observable<AlertTypeModel[]> {
        const params = new HttpParams().set('alertTypeKey', alertTypeKey.toString());
        return this.http.get<AlertTypeModel[]>(`${this._url}/alertType`, { params: params });
    }

    public getClientNotificationTypes(): Observable<ClientAlertTypeModel[]> {
        return this.http.get<ClientAlertTypeModel[]>(`${this._url}/types/client`);
    }

    public getUserNotificationTypes(): Observable<UserAlertTypeModel[]> {
        return this.http.get<UserAlertTypeModel[]>(`${this._url}/types/user`);
    }
}
